import { useMemo, useState } from 'react';
import {
    useGetMemberMutation,
    useUpdateMemberFinancialBenefitMutation,
    useAddMemberFinancialBenefitMutation,
    useGetMemberFinancialBenefitsMutation,
    useGetMemberArchivedFinancialBenefitsMutation,
    useUpdateMemberFinancialBenefitScheduleMutation,
    useToggleArchiveMemberFinancialBenefitMutation
} from '@services/member/member/member.endpoints';
import { Member, FinancialBenefitSchedule, FinancialBenefit, FinancialBenefitScheduleToUpdate } from '@services/member/member/member.types';

type FinancialBenefitUpdateValues = {
    code: string;
    amount: number;
    checkNumber: string;
    additionalNumber: string;
    requestedAt: string;
};
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { useGetFinancialBenefitsMutation } from '@services/member/list/financial-benefits/financial-benefits.endpoints';
import { FinancialBenefitListItem } from '@services/member/list/financial-benefits/financial-benefits.types';

export const useDetailsFinancialBenefitsVM = () => {
    const { id } = useParams();
    const [member, setMember] = useState<Member>();
    const financialBenefitScheduleColumnHelper = createColumnHelper<FinancialBenefitSchedule>();
    const financialBenefitColumnHelper = createColumnHelper<FinancialBenefit>();
    const [needReload, setNeedReload] = useState<boolean>();
    const [totalFinancialBenefits, setTotalFinancialBenefits] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const MySwal = withReactContent(Swal);
    const [selectedFinancialBenefit, setSelectedFinancialBenefit] = useState<FinancialBenefit>();
    const [financialBenefits, setFinancialBenefits] = useState<FinancialBenefitListItem[]>([]);
    const [financialBenefitToUpdate, setFinancialBenefitToUpdate] = useState<FinancialBenefit>();

    const [
        getFinancialBenefits,
        {
            isLoading: isGetFinancialBenefitsLoading,
            isSuccess: isGetFinancialBenefitsSuccess,
            isError: isGetFinancialBenefitsError,
            error: getFinancialBenefitsError,
            data: getFinancialBenefitsData
        }
    ] = useGetFinancialBenefitsMutation();

    const [
        updateMemberFinancialBenefitSchedule,
        { isSuccess: isUpdateMemberFinancialBenefitScheduleSuccess, data: getUpdateMemberFinancialBenefitScheduleData }
    ] = useUpdateMemberFinancialBenefitScheduleMutation();

    const [updateMemberFinancialBenefit, { isSuccess: isUpdateMemberFinancialBenefitSuccess, data: updateMemberFinancialBenefitData }] =
        useUpdateMemberFinancialBenefitMutation();

    const [addMemberFinancialBenefit, { isSuccess: isAddMemberFinancialBenefitSuccess }] = useAddMemberFinancialBenefitMutation();

    const [toggleArchiveMemberFinancialBenefit, { isSuccess: isToggleArchiveMemberFinancialBenefitSuccess }] =
        useToggleArchiveMemberFinancialBenefitMutation();

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isUninitialized: isGetMemberUninitialized,
            isError: isGetMemberError,
            isSuccess: isGetMemberSuccess,
            error: getMemberError,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();

    useEffect(() => {
        getMember({ id: id as string });
        getFinancialBenefits();
    }, []);

    useEffect(() => {
        if (isAddMemberFinancialBenefitSuccess || isUpdateMemberFinancialBenefitSuccess || isToggleArchiveMemberFinancialBenefitSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isAddMemberFinancialBenefitSuccess, isUpdateMemberFinancialBenefitSuccess, isToggleArchiveMemberFinancialBenefitSuccess]);

    useEffect(() => {
        if (isUpdateMemberFinancialBenefitSuccess && updateMemberFinancialBenefitData) {
            if (selectedFinancialBenefit && selectedFinancialBenefit.id === updateMemberFinancialBenefitData.data.id) {
                setSelectedFinancialBenefit(updateMemberFinancialBenefitData.data);
            }
        }
    }, [isUpdateMemberFinancialBenefitSuccess, updateMemberFinancialBenefitData, selectedFinancialBenefit]);

    useEffect(() => {
        if (isUpdateMemberFinancialBenefitScheduleSuccess && getUpdateMemberFinancialBenefitScheduleData) {
            setNeedReload((prev) => !prev);
            setSelectedFinancialBenefit(getUpdateMemberFinancialBenefitScheduleData.data);
        }
    }, [isUpdateMemberFinancialBenefitScheduleSuccess, getUpdateMemberFinancialBenefitScheduleData]);

    useEffect(() => {
        if (isGetFinancialBenefitsLoading) return;
        if (isGetFinancialBenefitsError && getFinancialBenefitsError) {
            return;
        }
        if (isGetFinancialBenefitsSuccess && getFinancialBenefitsData) {
            setFinancialBenefits(getFinancialBenefitsData.data);
            return;
        }
    }, [
        isGetFinancialBenefitsLoading,
        isGetFinancialBenefitsSuccess,
        isGetFinancialBenefitsError,
        getFinancialBenefitsData,
        getFinancialBenefitsError
    ]);

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError && getMemberError) {
            return;
        }
        if (isGetMemberSuccess && getMemberResponse) {
            setMember(getMemberResponse.data);
            setSelectedFinancialBenefit(getMemberResponse.data.lastFinancialBenefit);
            return;
        }
    }, [isGetMemberLoading, isGetMemberError, isGetMemberSuccess, getMemberResponse, getMemberError]);

    const mutationHook = useMemo(() => {
        return isArchived ? useGetMemberArchivedFinancialBenefitsMutation : useGetMemberFinancialBenefitsMutation;
    }, [isArchived]);

    useEffect(() => {
        setTotalFinancialBenefits(0);
    }, [isArchived]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalFinancialBenefits(metadata.total);
        }
    };

    const handleToggleArchiveFinancialBenefit = (financialBenefitId: number, archive: boolean) => {
        if (!member?.id) return;
        MySwal.fire({
            title: archive
                ? 'Êtes-vous sûr de vouloir archiver cette prestation financière ?'
                : 'Êtes-vous sûr de vouloir désarchiver cette prestation financière ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                toggleArchiveMemberFinancialBenefit({ memberId: member.id, financialBenefitId, archived: archive });
            }
        });
    };

    const handleToggleArchiveFinancialBenefits = (lines: FinancialBenefit[], archive: boolean) => {
        if (!member?.id) return;
        MySwal.fire({
            title: archive
                ? 'Êtes-vous sûr de vouloir archiver ces prestations financières ?'
                : 'Êtes-vous sûr de vouloir désarchiver ces prestations financières ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    toggleArchiveMemberFinancialBenefit({ memberId: member.id, financialBenefitId: line.id, archived: archive });
                });
            }
        });
    };

    const handleUpdateFinancialBenefitSchedule = (data: FinancialBenefitScheduleToUpdate) => {
        updateMemberFinancialBenefitSchedule({
            id: String(id),
            financialBenefitId: selectedFinancialBenefit?.id ?? 0,
            financialBenefitScheduleId: data.id,
            data
        });
    };

    const handleUpdateFinancialBenefitComplete = (financialBenefit: FinancialBenefit, updatedValues: FinancialBenefitUpdateValues) => {
        if (!member?.id) return;

        const updatedFinancialBenefit = {
            ...financialBenefit,
            code: updatedValues.code,
            amount: updatedValues.amount,
            checkNumber: updatedValues.checkNumber,
            additionalNumber: updatedValues.additionalNumber,
            requestedAt: updatedValues.requestedAt
        };

        if (selectedFinancialBenefit && selectedFinancialBenefit.id === financialBenefit.id) {
            setSelectedFinancialBenefit(updatedFinancialBenefit);
        }

        updateMemberFinancialBenefit({
            id: member.id.toString(),
            financialBenefit: updatedFinancialBenefit
        });
    };

    const handleToggleUpdateFinancialBenefit = (financialBenefit: FinancialBenefit) => {
        setFinancialBenefitToUpdate(financialBenefit);
    };

    return {
        isLoading: isGetMemberUninitialized || isGetMemberLoading,
        isError: isGetMemberError && !member,
        member,
        financialBenefitScheduleColumnHelper,
        financialBenefitColumnHelper,
        totalFinancialBenefits,
        isArchived,
        handleMetadata,
        needReload,
        MySwal,
        addMemberFinancialBenefit,
        setIsArchived,
        id,
        mutationHook,
        selectedFinancialBenefit,
        setSelectedFinancialBenefit,
        handleUpdateFinancialBenefitSchedule,
        financialBenefits,
        handleToggleArchiveFinancialBenefit,
        handleToggleArchiveFinancialBenefits,
        handleToggleUpdateFinancialBenefit,
        financialBenefitToUpdate,
        handleUpdateFinancialBenefitComplete
    };
};
